// apps/client/src/ui/screens/catch.tsx (M1-T15) — the catch card (DESIGN.md "catch": icon, name, size, a full-width pun line; the
// title tab in the catcher's friend colour). It closes itself after a moment, unless the fish landed in your hand because your pockets
// were full: then it asks Drop / Let it go and stays until one is pressed. While a dialogue is open the card waits, hidden and with its
// timer stopped, so the two never show together and the card never covers the speaker's name tag (M1 playtest).
import { friendColors, getItem, type FishEntry, type StringKey } from '@whiskerton/content';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useCommands } from '../../game/instance';
import { useGame } from '../../state/store';
import { itemName, useT } from '../i18n';
import { useIcon } from '../items/useIcon';
import { Button, Panel } from '../kit';
import type { ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { catch: undefined } }

/** How long the card stays up when there is nothing to decide (a UI pace, like a toast's). */
const SHOW_MS = 2600;
/** Removes this card wherever it sits in the stack (a screen opened over it is never the one closed). */
const dismiss = () => useGame.setState((s) => ({ screens: s.screens.filter((x) => x.id !== 'catch'), fishingCatch: null }));

function Catch() {
  const t = useT();
  const cmd = useCommands();
  const { c, you, residents, talking } = useGame(useShallow((s) => ({ c: s.fishingCatch, you: s.you, residents: s.residents,
    talking: s.screens.some((x) => x.id === 'dialogue') })));
  const icon = useIcon(c?.catalogId ?? null);
  const live = c ? (you?.items.find((i) => i.id === c.item.id) ?? null) : null;
  const inHand = (live ?? c?.item)?.at.loc === 'hand';
  useEffect(() => {
    if (!c) { dismiss(); return; }
    if (inHand || talking) return;
    const timer = setTimeout(dismiss, SHOW_MS);
    return () => clearTimeout(timer);
  }, [c, inHand, talking]);
  if (!c || talking) return null;
  const entry = getItem(c.catalogId) as FishEntry;
  const color = you ? residents[you.resident]?.color : undefined;
  const fish = t(entry.name as StringKey);
  return (
    <div className="catch-layer">
      <div className="catch-card-wrap">
        <Panel title={t('fishing.caught', { fish })} tabColor={color ? friendColors[color].tag : undefined} className="catch-card">
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '1.6cqw', alignItems: 'center' }}>
            <img src={icon} alt="" style={{ width: '9cqw', maxWidth: 96, aspectRatio: '1' }} />
            <div>
              <div className="title" style={{ fontWeight: 900, fontSize: '1.35em' }}>{itemName(entry.name)}</div>
              <div>{t('fishing.size', { size: c.size })}</div>
            </div>
            <p style={{ gridColumn: '1 / -1', margin: 0, fontWeight: 800 }}>{t(entry.pun as StringKey)}</p>
            {inHand && (
              <>
                <p style={{ gridColumn: '1 / -1', margin: 0 }}>{t('fishing.handFull')}</p>
                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <Button variant="quiet" onClick={() => { void cmd.dropItem(c.item.id).then(() => { if (useGame.getState().you?.items.find((i) => i.id === c.item.id)?.at.loc !== 'hand') dismiss(); }); }}>{t('items.drop')}</Button>
                  <Button onClick={() => { void cmd.releaseFish(c.item.id).then(dismiss); }}>{t('items.release')}</Button>
                </div>
              </>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}
export const screen: ScreenDef<'catch'> = { id: 'catch', layer: 'overlay', component: () => <Catch /> };
